import React from "react";
import { ArrowDownRight, Camera, Play, Aperture, Heart, Users, Sparkles, ChevronDown } from "lucide-react";
import { profile, stats, brands } from "../mock";

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
      {/* decorative background blobs */}
      <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#F3E9DD] blur-3xl opacity-70" />
      <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-[#E7D8C6] blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 md:gap-8 items-center w-full">
        {/* Left copy */}
        <div className="relative z-10">
          {/* availability badge */}
          <div className="inline-flex items-center gap-2.5 rounded-full bg-[#FAF5EF] border border-[#E7D8C6] pl-3 pr-4 py-1.5 shadow-sm mb-7">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B0603F] opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#B0603F]" />
            </span>
            <span className="font-body text-[11px] tracking-wide-2 uppercase text-[#6B5D53]">
              Disponible pour de nouvelles collaborations
            </span>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-12 bg-[#B0603F]" />
            <span className="font-body text-xs tracking-luxe uppercase text-[#B0603F]">
              {profile.role}
            </span>
          </div>

          <h1 className="font-serif-display leading-[0.86] text-[#3A302A]">
            <span className="block text-7xl md:text-[8.5rem] font-light tracking-tight">Portfolio</span>
            <span className="block font-script text-6xl md:text-8xl text-[#B0603F] mt-1 md:mt-3">
              {profile.name}
            </span>
          </h1>

          <p className="mt-8 max-w-md font-body text-base md:text-lg text-[#6B5D53] leading-relaxed">
            Créatrice de contenu <em className="text-[#B0603F] not-italic font-medium">UGC</em> &amp;{" "}
            <em className="text-[#B0603F] not-italic font-medium">community manager</em> — je donne vie à votre
            marque à travers des contenus authentiques qui inspirent et convertissent.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("#projets")}
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3A302A] text-[#FAF5EF] text-sm tracking-wide-2 uppercase hover:bg-[#B0603F] transition-colors duration-300"
            >
              Voir mes projets
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <button
              onClick={() => scrollTo("#contact")}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#B79A80] text-[#3A302A] text-sm tracking-wide-2 uppercase hover:border-[#B0603F] hover:text-[#B0603F] transition-colors duration-300"
            >
              Travaillons ensemble
            </button>
          </div>

          {/* trust line */}
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="font-body text-[11px] tracking-wide-2 uppercase text-[#A98B72]">
              Ils m'ont fait confiance
            </span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {brands.slice(0, 4).map((b) => (
                <span key={b} className="font-serif-display italic text-lg text-[#6B5D53]">
                  {b}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-10 flex gap-8">
            {stats.map((s) => (
              <div key={s.label}>
                <div className="font-serif-display text-4xl text-[#B0603F]">{s.value}</div>
                <div className="font-body text-xs tracking-wide-2 uppercase text-[#6B5D53] mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right decorative panel */}
        <div className="relative flex justify-center md:justify-end">
          <div className="relative">
            <div className="arch w-[290px] h-[400px] md:w-[400px] md:h-[520px] shadow-[0_30px_70px_-20px_rgba(58,48,42,0.45)] bg-gradient-to-br from-[#C89A78] to-[#9C5A3C] relative overflow-hidden flex flex-col items-center justify-center text-center px-8">
              {/* decorative rings */}
              <div className="absolute inset-5 border border-[#FAF5EF]/25 arch" />
              <div className="absolute inset-10 border border-[#FAF5EF]/15 arch" />
              <Aperture size={46} className="text-[#FAF5EF]/90 mb-5" strokeWidth={1.2} />
              <span className="font-body text-[10px] tracking-luxe uppercase text-[#FAF5EF]/80">
                {profile.brand}
              </span>
              <span className="font-script text-6xl md:text-7xl text-[#FAF5EF] mt-2 leading-none">
                {profile.name}
              </span>
              <span className="h-px w-12 bg-[#FAF5EF]/50 my-4" />
              <span className="font-body text-xs tracking-wide-2 uppercase text-[#FAF5EF]/85">
                UGC · Photo · Vidéo
              </span>
            </div>

            {/* floating cards */}
            <div className="absolute -left-6 md:-left-10 top-12 bg-[#FAF5EF] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 animate-floaty">
              <span className="w-9 h-9 rounded-full bg-[#F3E9DD] flex items-center justify-center text-[#B0603F]">
                <Camera size={18} />
              </span>
              <div className="leading-tight">
                <div className="font-serif-display text-lg text-[#3A302A]">UGC &amp; Photo</div>
                <div className="font-body text-[10px] tracking-wide-2 uppercase text-[#6B5D53]">Content Creator</div>
              </div>
            </div>

            <div className="absolute -right-4 md:-right-8 top-1/2 -translate-y-1/2 bg-[#FAF5EF] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 animate-floaty" style={{ animationDelay: "0.8s" }}>
              <span className="w-9 h-9 rounded-full bg-[#F3E9DD] flex items-center justify-center text-[#B0603F]">
                <Users size={18} />
              </span>
              <div className="leading-tight">
                <div className="font-serif-display text-lg text-[#3A302A]">Community</div>
                <div className="font-body text-[10px] tracking-wide-2 uppercase text-[#6B5D53]">Management</div>
              </div>
            </div>

            <div className="absolute -left-4 md:-left-8 bottom-12 bg-[#B0603F] text-[#FAF5EF] rounded-2xl shadow-lg px-4 py-3 flex items-center gap-3 animate-floaty" style={{ animationDelay: "1.5s" }}>
              <span className="w-9 h-9 rounded-full bg-[#FAF5EF]/20 flex items-center justify-center">
                <Heart size={16} fill="currentColor" />
              </span>
              <div className="leading-tight">
                <div className="font-serif-display text-lg">Mariages</div>
                <div className="font-body text-[10px] tracking-wide-2 uppercase opacity-80">émotions capturées</div>
              </div>
            </div>

            {/* sparkle accent */}
            <Sparkles size={28} className="absolute -top-2 right-10 text-[#C88A63] animate-floaty" style={{ animationDelay: "2s" }} />
          </div>
        </div>
      </div>

      {/* scroll indicator */}
      <button
        onClick={() => scrollTo("#projets")}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-[#A98B72] hover:text-[#B0603F] transition-colors"
        aria-label="Défiler"
      >
        <span className="font-body text-[10px] tracking-luxe uppercase">Découvrir</span>
        <ChevronDown size={20} className="animate-bounce" />
      </button>
    </section>
  );
}
